import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { environment } from '@ecs-environment';
import { isStringArrayWithEntries, mediaQueries } from '@ecs-helpers';
import { SearchLimitNotificationService } from '@ecs-services/search-limit-notification.service';
import { SearchStoreService } from '@ecs-services/search/search-store.service';
import {
    SEARCH_TERM_SEPARATOR,
    isFreeTextResult,
    isSparePartsDataResult,
    isTypeDesignationResult,
} from '@ecs-services/search/search.helper';
import {
    FreeTextSearchResult,
    SearchResultType,
    TypeDesignationSearchResult,
} from '@ecs-services/search/search.interface';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CountryParamService, LanguageService, OnDestroyBase, WithLoadingPipe } from '@os-components';
import {
    SewButton,
    SewHeadline,
    SewNotificationStack,
    SewSkeleton,
} from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { distinct, filter, map, takeUntil } from 'rxjs/operators';
import { SearchComponent } from '../search/search.component';
import { ElectronicsComponent } from './components/electronic-supplies/electronics.component';
import { GearOilComponent } from './components/gear-oil-component/gear-oil.component';
import { RequestReplacementProductComponent } from './components/request-replacement-product/request-replacement-product.component';
import { RequestServiceComponent } from './components/request-service/request-service.component';
import { SuppliesServicesComponent } from './components/supplies-services/supplies-services.component';
import {
    SUPPLIES_SERVICE_TAB_SEARCH,
    SuppliesServicesTab,
    TabParamName,
} from './components/supplies-services/supplies-services.types';
import { WhatsNewComponent } from './components/whats-new/whats-new.component';

@Component({
    selector: 'ecs-landing-page',
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss'],
    imports: [
        TranslateDirective,
        SearchComponent,
        NgClass,
        RouterOutlet,
        RequestReplacementProductComponent,
        SuppliesServicesComponent,
        NgTemplateOutlet,
        WhatsNewComponent,
        AsyncPipe,
        TranslatePipe,
        WithLoadingPipe,
        SewHeadline,
        SewButton,
        SewNotificationStack,
        SewSkeleton,
    ],
})
export class LandingPageComponent extends OnDestroyBase implements OnInit, AfterViewInit, OnDestroy {
    public isMobile = false;
    public skeletonListRowCount = [...Array(6).keys()];
    public skeletonServicesContentElements = [...Array(1).keys()];
    public searchResultType?: string;
    public searchResultTypes = SearchResultType;
    public isVisible?: Observable<boolean>;
    public activeTab = 0;
    public availableTabs?: Observable<SuppliesServicesTab[]>;

    @ViewChild('notificationStack')
    private notificationStack!: ElementRef<HTMLSewNotificationStackElement>;

    public constructor(
        private searchStore: SearchStoreService,
        private breakpointObserver: BreakpointObserver,
        private translateService: TranslateService,
        private languageService: LanguageService,
        private searchLimitNotificationService: SearchLimitNotificationService,
        private activatedRoute: ActivatedRoute,
        private paramService: CountryParamService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.breakpointObserver
            .observe(mediaQueries.mq3)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(({ matches }) => {
                this.isMobile = !matches;
            });

        this.searchStore
            .getSearchTerm()
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                if (this.notificationStack) {
                    this.removeAllNotification();
                }
            });
        this.isVisible = this.searchLimitNotificationService.getVisibility();
        this.initializeSuppliesServicesTabs();
    }

    public ngAfterViewInit(): void {
        const searchResult$ = this.searchStore.getSearchResult().pipe(
            takeUntil(this.destroySubject),
            filter(() => this.notificationStack !== null)
        );

        searchResult$
            .pipe(
                filter(
                    (
                        searchResult
                    ): searchResult is (FreeTextSearchResult | TypeDesignationSearchResult) & {
                        spareParts: string[];
                    } =>
                        (isFreeTextResult(searchResult) || isTypeDesignationResult(searchResult)) &&
                        isStringArrayWithEntries(searchResult.spareParts)
                )
            )
            .subscribe((searchResult) => {
                this.searchResultType = searchResult.type;
                const limitExceededCount = searchResult.limitExceeded ? searchResult.spareParts.length : null;
                if (limitExceededCount && limitExceededCount >= 0) {
                    this.removeAllNotification().then(() => {
                        const event: CustomEvent = new CustomEvent('addNotification', {
                            detail: this.createFreeTextNotification(limitExceededCount),
                        });
                        this.notificationStack.nativeElement.dispatchEvent(event);
                    });
                }
            });

        searchResult$
            .pipe(
                /*
                 * filters out `isSparePartResult` even though this would be also valid.
                 *
                 * Otherwise the ui would flicker in case of search `041041,01061526,5432`:
                 * - https://www.sew-d.de/os/api/ecs/search?search=041041,01061526,5432 -> responds with errors: 5432
                 * - https://www.sew-d.de/os/api/ecs/spare-parts -> responds with errors: 01061526 (get's merged with first request)
                 * as existing notifications can't be edited and have to be removed...
                 *
                 */
                filter(isSparePartsDataResult),
                distinct((searchResult) => searchResult.errors)
            )
            .subscribe((searchResult) => {
                if (searchResult.spareParts.length && searchResult.errors && searchResult.errors.length) {
                    const searchTerms = searchResult.errors.join(SEARCH_TERM_SEPARATOR);
                    this.removeAllNotification().then(() => {
                        const event: CustomEvent = new CustomEvent('addNotification', {
                            detail: this.createSparePartsNotification(searchTerms),
                        });
                        this.notificationStack.nativeElement.dispatchEvent(event);
                    });
                }
            });
    }

    private createFreeTextNotification(limitExceededCount: number) {
        const notification = document.createElement('sew-notification');
        notification.headline = this.translateService.instant('SEARCH.LIMIT_EXCEEDED.HEADLINE');
        notification.type = 'info';
        notification.innerHTML = this.translateService.instant('SEARCH.LIMIT_EXCEEDED.TEXT', {
            limitExceededCount,
        });

        return notification;
    }

    private createSparePartsNotification(searchTerms: string) {
        const notification = document.createElement('sew-notification');

        notification.type = 'info';
        notification.headline = this.translateService.instant(
            'ECS.NOTIFICATION.HEADLINE.PARTIALLY_NO_MATCHES_FOUND'
        );

        const p = this.createParagraph(searchTerms);
        notification.appendChild(p);
        const sewLink = document.createElement('sew-link');
        const a = this.createHtmlLink(searchTerms);
        sewLink.appendChild(a);
        notification.appendChild(sewLink);

        return notification;
    }

    private createParagraph(searchTerms: string) {
        const p = document.createElement('p');
        const message = document.createTextNode(
            this.translateService.instant('SEARCH.PARTIAL_ERROR.TEXT', {
                searchTerms,
            })
        );
        p.appendChild(message);

        return p;
    }

    private createHtmlLink(searchTerms: string) {
        const a = document.createElement('a');
        const text = this.translateService.instant('SEARCH.ACTION.OPEN_CONTACT_FORM.LINK');
        a.appendChild(document.createTextNode(text));
        a.title = text;
        const subject = encodeURIComponent(
            this.translateService.instant('SEARCH.ACTION.OPEN_CONTACT_FORM.SUBJECT')
        );
        a.target = '_blank';
        const locale = this.languageService.language.toLowerCase();
        a.href = `${environment.baseURLs.api.domain}/${locale}/technical_pages/forwarding/forwarding_os_contact_form.html?subject=${subject}&mailbody=${searchTerms}`;

        return a;
    }

    private removeAllNotification() {
        return this.notificationStack.nativeElement?.clearAllNotifications();
    }

    private initializeSuppliesServicesTabs() {
        this.availableTabs = this.paramService.getParams('ECS', SUPPLIES_SERVICE_TAB_SEARCH).pipe(
            map((tabsParam) => {
                const tabs: SuppliesServicesTab[] = [];
                tabsParam.map((tabParam) => {
                    if (tabParam.value === true) {
                        switch (tabParam.parameter) {
                            // TODO currently the components to be displayed as tab content aren't
                            // implemented -> outcomment the following lines if the respective
                            // components are available
                            case TabParamName.GEAR_OIL:
                                tabs.push({
                                    tabTitle: 'STARTPAGE.TAB.GEAR_OIL',
                                    icon: 'oil',
                                    urlParam: 'gearoil',
                                    component: GearOilComponent,
                                    index: 0,
                                });
                                break;
                            // case TabParamName.MAINTENANCE:
                            //     tabs.push({
                            //         tabTitle: 'STARTPAGE.TAB.MAINTENANCE',
                            //         icon: 'maintenance',
                            //         urlParam: 'kits',
                            //         component: MaintenanceComponent,
                            //     });
                            //     break;
                            // case TabParamName.MECHANICS:
                            //     tabs.push({
                            //         tabTitle: 'STARTPAGE.TAB.MECHANICS',
                            //         icon: 'drive_motor',
                            //         urlParam: 'mechanics',
                            //         component: MechanicsComponent,
                            //     });
                            //     break;
                            case TabParamName.SERVICE:
                                tabs.push({
                                    tabTitle: 'STARTPAGE.TAB.SERVICE',
                                    icon: 'tools',
                                    urlParam: 'service',
                                    component: RequestServiceComponent,
                                    index: 1,
                                });
                                break;
                            case TabParamName.ELECTRONICS:
                                tabs.push({
                                    tabTitle: 'STARTPAGE.TAB.ELECTRONICS',
                                    icon: 'frequency-inverter',
                                    urlParam: 'electronics',
                                    component: ElectronicsComponent,
                                    index: 2,
                                });
                                break;
                            default:
                                break;
                        }
                    }
                });
                tabs.sort((a, b) => a.index - b.index);
                const tabIndex = tabs.findIndex(
                    (tab) => tab.urlParam === this.activatedRoute.snapshot.queryParamMap.get('tab')
                );
                this.activeTab = tabIndex === -1 ? 0 : tabIndex;

                return tabs;
            })
        );
    }
}
